/**
 * Essentials qualification check.
 *
 * Gates the cart page's checkout submit on a yes/no question when an
 * "Essentials" variant is in the cart. A "yes" answer (disqualifying)
 * swaps the Essentials line for the matching "Basic" line, preserving
 * the correct selling plan, then reloads so the cart page reflects the
 * new contents.
 *
 * Does not touch Date of Birth capture or the existing Privacy
 * Policy / Terms agreement checkbox — those are separate.
 */
(function () {
  function getConfig(container) {
    return {
      essentialsIndividual: container.dataset.essentialsIndividualVariant,
      essentialsFamily: container.dataset.essentialsFamilyVariant,
      basicIndividual: container.dataset.basicIndividualVariant,
      basicIndividualPlan: container.dataset.basicIndividualSellingPlan,
      basicFamily: container.dataset.basicFamilyVariant,
      basicFamilyPlan: container.dataset.basicFamilySellingPlan
    };
  }

  function findFlaggedItem(cart, config) {
    return (cart.items || []).find(function (item) {
      var variantId = String(item.variant_id);
      return variantId === config.essentialsIndividual || variantId === config.essentialsFamily;
    });
  }

  function getReplacement(flaggedItem, config) {
    if (String(flaggedItem.variant_id) === config.essentialsIndividual) {
      return { variantId: config.basicIndividual, sellingPlan: config.basicIndividualPlan };
    }
    return { variantId: config.basicFamily, sellingPlan: config.basicFamilyPlan };
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (res) { return res.json(); });
  }

  function init() {
    var container = document.querySelector('.essentials-qualification');
    var form = document.getElementById('cart');
    var checkoutButton = document.getElementById('checkout');
    if (!container || !form || !checkoutButton) return;

    var config = getConfig(container);
    var statusEl = container.querySelector('.essentials-qualification__status');
    var messageEl = container.querySelector('.essentials-qualification__message');
    var questionEl = container.querySelector('.essentials-qualification__question');
    var buttonsEl = container.querySelector('.essentials-qualification__buttons');
    var noBtn = container.querySelector('.essentials-qualification__no');
    var yesBtn = container.querySelector('.essentials-qualification__yes');

    var answered = false;
    var bypassNextSubmit = false;

    function submitFormProgrammatically() {
      bypassNextSubmit = true;
      if (form.requestSubmit) {
        // Passing checkoutButton here is required: requestSubmit() with no
        // argument does not carry forward which button triggered it, so
        // Shopify's server never sees name="checkout" and just refreshes
        // the cart page instead of routing to checkout.
        form.requestSubmit(checkoutButton);
      } else {
        // Old-browser fallback. plain .submit() cannot carry a button's
        // name/value under any circumstances, so on browsers without
        // requestSubmit support this will still land back on /cart rather
        // than checkout. Acceptable gap given requestSubmit is supported
        // by all current evergreen browsers.
        form.submit();
      }
    }

    // Intercept the cart form's submit (the Checkout button lives inside
    // this form) so we can check, at the moment of submission, whether a
    // flagged variant is present. This runs a preventDefault() only — it
    // never calls stopImmediatePropagation() — so any other submit
    // listener already on this form (e.g. one validating the Terms
    // checkbox) still runs normally alongside this one.
    form.addEventListener('submit', function (e) {
      if (bypassNextSubmit) {
        bypassNextSubmit = false;
        return;
      }
      if (answered) {
        return;
      }

      e.preventDefault();

      fetchCart()
        .then(function (cart) {
          var flagged = findFlaggedItem(cart, config);
          if (!flagged) {
            answered = true;
            submitFormProgrammatically();
            return;
          }
          container.style.display = '';
          container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        })
        .catch(function () {
          statusEl.textContent = 'We could not check your cart. Please try again.';
        });
    });

    noBtn.addEventListener('click', function () {
      answered = true;
      container.style.display = 'none';
      submitFormProgrammatically();
    });

    yesBtn.addEventListener('click', function () {
      noBtn.disabled = true;
      yesBtn.disabled = true;
      statusEl.textContent = 'Updating your cart…';

      fetchCart()
        .then(function (cart) {
          var flagged = findFlaggedItem(cart, config);
          if (!flagged) {
            // Cart changed since the question appeared and no longer
            // contains a flagged item — nothing to swap.
            answered = true;
            container.style.display = 'none';
            submitFormProgrammatically();
            return;
          }

          var replacement = getReplacement(flagged, config);

          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: flagged.key, quantity: 0 })
          })
            .then(function (res) { return res.json(); })
            .then(function (changeResult) {
              if (changeResult && changeResult.status) {
                throw new Error(changeResult.description || 'Could not remove the current plan.');
              }
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  items: [
                    {
                      id: replacement.variantId,
                      quantity: 1,
                      selling_plan: replacement.sellingPlan
                    }
                  ]
                })
              });
            })
            .then(function (res) { return res.json(); })
            .then(function (addResult) {
              if (addResult && addResult.status) {
                throw new Error(addResult.description || 'Could not add the Basic plan.');
              }
              return fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  attributes: {
                    'Cardiology Oncology Screening': 'Disqualified - switched to Basic'
                  }
                })
              }).catch(function () {
                // Deliberately swallowed: the cart swap already succeeded
                // by this point, and this attribute is internal tracking
                // only. A failure here shouldn't surface as an error to a
                // customer whose swap actually worked correctly.
              });
            })
            .then(function () {
              statusEl.textContent = '';
              questionEl.style.display = 'none';
              buttonsEl.style.display = 'none';
              messageEl.style.display = '';
              setTimeout(function () {
                window.location.reload();
              }, 5000);
            });
        })
        .catch(function (err) {
          statusEl.textContent =
            'Something went wrong updating your cart. Please refresh the page and try again, or contact support.';
          noBtn.disabled = false;
          yesBtn.disabled = false;
          if (window.console) {
            console.error('Essentials qualification swap failed:', err);
          }
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();